<template>
  <div class="fermi-distribution-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$f(E) = 1 / [e^{(E-Ef)/kT} + 1]$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 400 220">
          <g transform="translate(0, 10)">
            <!-- Grid Lines -->
            <g opacity="0.1" stroke="#fff" stroke-width="0.5">
               <line v-for="i in 10" :key="'g'+i" x1="40" :y1="i*20" x2="360" :y2="i*20" />
            </g>

            <!-- Coordinates -->
            <line x1="40" y1="180" x2="350" y2="180" stroke="#94a3b8" /> <!-- Energy axis -->
            <line x1="40" y1="180" x2="40" y2="10" stroke="#94a3b8" /> <!-- Probability axis -->
            
            <!-- Markers & Text -->
            <text x="360" y="185" fill="#94a3b8" font-size="10">能量 E</text>
            <text x="15" y="20" fill="#94a3b8" font-size="10">f(E)</text>
            <text x="25" y="182" fill="#94a3b8" font-size="10">0</text>
            <text x="25" y="45" fill="#94a3b8" font-size="10">1</text>
            <line x1="38" y1="40" x2="42" y2="40" stroke="#94a3b8" />

            <!-- The Theoretical Curve -->
            <path :d="fermiPath" fill="none" stroke="#06b6d4" stroke-width="3" stroke-linecap="round" />
            
            <!-- Ef marker -->
            <line :x1="Ef" y1="180" :x2="Ef" y2="20" stroke="#ef4444" stroke-dasharray="4,4" stroke-width="1.5" />
            <text :x="Ef - 10" y="200" fill="#ef4444" font-size="12" font-weight="bold">E_f</text>

            <!-- Electron Energy State Toggles (Statistical simulation) -->
            <g v-for="(state, i) in energyStates" :key="i">
               <!-- State holder line -->
               <line :x1="state.E" y1="180" :x2="state.E" y2="175" stroke="#475569" stroke-width="2" />
               <!-- Animated electron occupation -->
               <circle :cx="state.E" cy="184" r="3" 
                       :fill="state.occupied ? '#10b981' : '#1e293b'" 
                       :stroke="state.occupied ? '#059669' : '#334155'" 
                       stroke-width="1.5">
                  <animate v-if="state.animatePulse" attributeName="opacity" values="0.4;1;0.4" dur="0.5s" repeatCount="indefinite" />
               </circle>
            </g>
          </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>系统绝对温度 T</label>
              <span class="val">{{ temperature }} <small>K</small></span>
           </div>
           <div class="hud-item orange">
              <label>费米面边缘宽化</label>
              <span class="val">~ {{ kT.toFixed(2) }} <small>eV</small></span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="param-row">
                 <label style="font-size:11px;color:#64748b;font-weight:700;">调节半导体/金属的工作温度 (K)</label>
                 <el-slider v-model="temperature" :min="0" :max="3000" :step="10" size="small"></el-slider>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>真实物理计算：</strong>仿真展现了强电子统计学规律——费米-狄拉克分布。因**保里不相容原理**，每一能态只能抢占1个电子。在 <code>T = 0 K</code> 时，能量低于 <code>E_f</code>（费米能级）的能态电子占据概率绝对为 1（全满），像一个阶梯式悬崖；随着温度 <code>T</code> 升高，热激发导致部分顶部电子跃迁至高能级，分布曲线在 <code>E_f</code> 附近平滑延展展宽。观察X轴上微观量子态的小正方形闪烁情况，可直观体会随概率波动的粒子占据行为！</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FermiDistributionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      temperature: 300,
      Ef: 200,
      k_Boltz: 0.05, // simulated scaling constant
      energyStates: [],
      timer: null
    }
  },
  computed: {
    kT() {
      // Representation of kT spread
      return this.temperature * this.k_Boltz * 0.1;
    },
    fermiPath() {
       let pts = [];
       for (let x = 40; x <= 350; x++) {
          let e = x;
          let prob;
          if (this.temperature === 0) {
             prob = e < this.Ef ? 1 : (e === this.Ef ? 0.5 : 0);
          } else {
             let kT_scaled = this.temperature * this.k_Boltz;
             prob = 1 / (Math.exp((e - this.Ef) / kT_scaled) + 1);
          }
          let y = 180 - prob * 140;
          if (!isNaN(y)) pts.push(`${x},${y.toFixed(1)}`);
       }
       return pts.length > 0 ? "M " + pts.join(" L ") : "";
    }
  },
  methods: {
    initEnergyStates() {
       this.energyStates = [];
       for (let x = 50; x <= 340; x += 6) {
          this.energyStates.push({ E: x, occupied: false, animatePulse: false });
       }
    },
    simulateOccupation() {
       let kT_scaled = this.temperature * this.k_Boltz;
       
       this.energyStates.forEach(s => {
          let prob;
          if (this.temperature === 0) {
             prob = s.E < this.Ef ? 1 : 0;
          } else {
             prob = 1 / (Math.exp((s.E - this.Ef) / kT_scaled) + 1);
          }
          
          let roll = Math.random();
          s.occupied = roll < prob;

          // If prob is between 0.1 and 0.9, it's highly dynamic, show pulse indicator occasionally
          s.animatePulse = prob > 0.05 && prob < 0.95;
       });
    },
    tick() {
       // Run simulation loop
       this.simulateOccupation();
       // Use a slower interval for visual blinking rate rather than 60fps
       this.timer = setTimeout(() => {
          requestAnimationFrame(this.tick);
       }, 100); 
    }
  },
  mounted() { 
     this.initEnergyStates();
     this.tick(); 
  },
  beforeDestroy() { 
     if(this.timer) clearTimeout(this.timer); 
  }
}
</script>

<style scoped lang="scss">
.fermi-distribution-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-row { flex-direction: row; } .items-center { align-items: center; } .gap-4 { gap: 1rem; } .flex-1 { flex: 1; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #2563eb; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } code { background: #e2e8f0; padding: 2px 4px; border-radius: 4px; font-family: monospace; } } }
</style>
